import React, { useRef, useEffect, useState } from 'react';
import { Send, Bot, User } from 'lucide-react';
import { Policy } from '../../services/firestoreService';
import { Button } from '../ui/button';

interface Message {
  id: string;
  content: string;
  sender: 'user' | 'ai';
  timestamp: Date;
  isTyping?: boolean;
}

interface AIBotTabProps {
  policies: Policy[];
  messages: Message[];
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  inputMessage: string;
  setInputMessage: React.Dispatch<React.SetStateAction<string>>;
  typeMessage: (messageId: string, fullText: string) => void;
  handleSendMessage: (e: React.FormEvent) => Promise<void>;
  quickQuestions: string[];
  scrollPosition: number;
  scrollContainerRef: React.RefObject<HTMLDivElement>;
  scrollQuestions: (direction: 'left' | 'right') => void;
  chatContainerRef: React.RefObject<HTMLDivElement>;
  isUserScrolling: boolean;
  shouldAutoScroll: boolean;
  handleScroll: () => void;
  scrollToBottom: (force?: boolean) => void;
  isAIReplying: boolean;
}

const AIBotTab: React.FC<AIBotTabProps> = ({ 
  policies,
  messages,
  setMessages,
  inputMessage,
  setInputMessage,
  typeMessage,
  handleSendMessage,
  quickQuestions,
  scrollPosition,
  scrollContainerRef,
  scrollQuestions,
  chatContainerRef,
  isUserScrolling,
  shouldAutoScroll,
  handleScroll,
  scrollToBottom,
  isAIReplying
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);

  // 檢測鍵盤彈出/收起
  useEffect(() => {
    const handleResize = () => {
      const currentViewportHeight = window.visualViewport?.height || window.innerHeight;
      
      // 如果視窗高度減少超過150px，認為鍵盤彈出
      const keyboardThreshold = 150;
      const isKeyboardVisible = (window.innerHeight - currentViewportHeight) > keyboardThreshold;
      
      setIsKeyboardOpen(isKeyboardVisible);
      
      // 鍵盤彈出時滾動到底部
      if (isKeyboardVisible && chatContainerRef.current) {
        setTimeout(() => {
          if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
          }
        }, 100);
      }
    };

    // 監聽視窗大小變化
    window.addEventListener('resize', handleResize);
    
    // 監聽視覺視窗變化（更準確的鍵盤檢測）
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleResize);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleResize);
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // 確保聊天區域在組件加載時滾動到正確位置
  useEffect(() => {
    if (chatContainerRef.current) {
      // 滾動到最新消息
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // 輸入欄位獲得焦點時滾動到底部
  const handleInputFocus = () => {
    setTimeout(() => {
      if (chatContainerRef.current) {
        chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
      }
    }, 300); // 等待鍵盤動畫完成
  };

  return (
    <div className="flex flex-col h-full">
      {/* 聊天區域 - 參考桌面版設計 */}
      <div 
        ref={chatContainerRef}
        className="flex-1 overflow-y-auto px-6 py-4 space-y-4"
        style={{ 
          minHeight: 0,
          paddingBottom: isKeyboardOpen ? '20px' : '0px'
        }}
        onScroll={handleScroll}
      >
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`flex items-start space-x-3 max-w-[90%] ${
                message.sender === 'user' ? 'flex-row-reverse space-x-reverse' : ''
              }`}
            >
              <div
                className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                  message.sender === 'user'
                    ? 'text-white'
                    : 'text-white'
                }`}
                style={{
                  backgroundColor: message.sender === 'user' ? 'var(--charcoal)' : 'var(--light-gray)'
                }}
              >
                {message.sender === 'user' ? (
                  <User className="h-5 w-5" />
                ) : (
                  <Bot className="h-5 w-5" />
                )}
              </div>
              <div
                className={`px-4 py-3 rounded-2xl ${
                  message.sender === 'user'
                    ? 'bg-white shadow-sm'
                    : 'bg-white'
                }`}
                style={{ border: '1px solid var(--card-shadow)' }}
              >
              {message.sender === 'ai' && message.isTyping && !message.content ? (
                <div className="flex items-center space-x-1">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: 'var(--medium-gray)', animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: 'var(--medium-gray)', animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: 'var(--medium-gray)', animationDelay: '300ms' }}></div>
                  </div>
                </div>
              ) : (
                <p className="text-body whitespace-pre-wrap leading-relaxed">
                  {message.content}
                  {message.sender === 'ai' && message.isTyping && message.content && (
                    <span className="inline-block w-0.5 h-4 bg-gray-600 ml-0.5" style={{
                      animation: 'blink 1s infinite'
                    }}></span>
                  )}
                </p>
              )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* 建議問題和輸入區域 - 位於聊天內容下方 */}
      <div className="p-4">
        {/* 圓角方形容器 */}
        <div 
          className="bg-white shadow-lg w-full"
          style={{ 
            boxShadow: '0 4px 20px var(--card-shadow)',
            background: 'var(--card-gradient)',
            borderRadius: '16px 16px 16px 16px', // 明確設置四個角的圓角
            overflow: 'hidden' // 確保圓角正確顯示
          }}
        >
          {/* Quick Questions - 與桌面版相同的設計 */}
          <div className="px-4 py-3 border-b flex items-center relative" style={{ borderColor: 'var(--card-shadow)' }}>
          {/* 左箭頭 */}
          <button
            onClick={() => scrollQuestions('left')}
            className="absolute left-2 z-10 p-2 rounded-full bg-white shadow-md hover:bg-gray-50 transition-colors"
            style={{ display: scrollPosition > 0 ? 'block' : 'none' }}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          
          {/* 右箭頭 */}
          <button
            onClick={() => scrollQuestions('right')}
            className="absolute right-2 z-10 p-2 rounded-full bg-white shadow-md hover:bg-gray-50 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>

          {/* 滾動容器 */}
          <div 
            ref={scrollContainerRef}
            className="flex gap-2 w-full overflow-x-auto scrollbar-hide"
            style={{ 
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              paddingLeft: scrollPosition > 0 ? '40px' : '0',
              paddingRight: '40px'
            }}
          >
          {quickQuestions.map((question) => (
            <Button
              key={question}
              variant="outline"
              size="sm"
              onClick={() => setInputMessage(question)}
              className="badge-primary text-xs px-3 py-2 h-8 flex items-center justify-center whitespace-nowrap flex-shrink-0"
            >
              {question}
            </Button>
          ))}
        </div>

          {/* 右側漸層遮罩 */}
          <div 
            className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-white to-transparent pointer-events-none"
          ></div>
        </div>

          {/* 輸入區域 */}
          <div className="px-4 py-3">
            <form onSubmit={handleSendMessage} className="flex items-end space-x-3">
              <div className="flex-1">
                <input
                  ref={inputRef}
                  type="text"
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onFocus={handleInputFocus}
                  placeholder="Ask me about your insurance..."
                  className="w-full px-4 py-3 border rounded-2xl focus:outline-none focus:ring-2 focus:border-transparent resize-none"
                  style={{ 
                    borderColor: 'var(--card-shadow)',
                    boxShadow: '0 2px 8px var(--input-shadow)',
                    fontSize: '16px' // 防止iOS Safari自動縮放
                  }}
                />
              </div>
              <button
                type="submit"
                disabled={!inputMessage.trim() || isAIReplying}
                className="w-11 h-11 text-white rounded-2xl flex items-center justify-center disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
                style={{
                  backgroundColor: 'var(--charcoal)'
                }}
              >
                <Send className="w-5 h-5" />
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIBotTab;
